import React from 'react';
import { User } from '../../../types';
import styles from '../../../styles/UserManagement.module.css';
import tableStyles from '../../../styles/Table.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import SkeletonLoader from '../../UI/SkeletonLoader';
import EmptyState from '../../UI/EmptyState';

interface UserTableProps {
    users: User[];
    loading: boolean;
    selectedUserIds: Set<string>;
    filters: { search: string; role: string; status: string };
    getUserName: (user: User) => string;
    formatDesignation: (designation?: string) => string;
    onUserSelect: (id: string, checked: boolean) => void;
    onEdit: (user: User) => void;
    onToggleStatus: (id: string, isActive: boolean) => void;
    onDelete: (id: string) => void;

    onShowAdd: () => void;
    onNavigatePerformance: (id: string) => void;
    isAllSelected: boolean;
    isIndeterminate: boolean;
    onSelectAll: (checked: boolean) => void;
}

const UserTable: React.FC<UserTableProps> = ({
    users,
    loading,
    selectedUserIds,
    filters,
    getUserName,
    formatDesignation,
    onUserSelect,
    onEdit,
    onToggleStatus,
    onDelete,
    onShowAdd,
    onNavigatePerformance,
    isAllSelected,
    isIndeterminate,
    onSelectAll
}) => {
    if (loading) {
        return (
            <div className={styles.tableContainer}>
                <SkeletonLoader count={5} variant="table" />
            </div>
        );
    }

    if (users.length === 0) {
        return (
            <EmptyState
                title="No Users Found"
                description={
                    filters.search || filters.role || filters.status
                        ? "No users match your current filters. Try adjusting your search criteria or clear the filters."
                        : "Get started by adding your first user to the system."
                }
                action={
                    !filters.search && !filters.role && !filters.status
                        ? {
                            label: "Add First User",
                            onClick: onShowAdd
                        }
                        : undefined
                }
            />
        );
    }

    return (
        <>
            {/* Desktop Table */}
            <div className={`${styles.tableContainer} ${styles.desktopTableContainer}`}>
                <table className={tableStyles.Table}>
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}>
                                <input
                                    type="checkbox"
                                    className={styles.rowCheckbox}
                                    checked={isAllSelected}
                                    ref={input => { if (input) input.indeterminate = isIndeterminate; }}
                                    onChange={(e) => onSelectAll(e.target.checked)}
                                />
                            </th>
                            <th>User</th>
                            <th>Designation</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id} data-userid={user.id}>
                                <td>
                                    <input
                                        type="checkbox"
                                        className={styles.rowCheckbox}
                                        checked={selectedUserIds.has(user.id)}
                                        onChange={(e) => onUserSelect(user.id, e.target.checked)}
                                    />
                                </td>
                                <td>
                                    <div className={tableStyles.tableCellContent}>
                                        <div className={tableStyles.tableCellTitle}>{getUserName(user)}</div>
                                        <div className={tableStyles.tableCellSubtitle}>{user.email}</div>
                                    </div>
                                </td>
                                <td style={{ textWrap: 'nowrap' }}>
                                    <span className={`${tableStyles.tableBadge} ${tableStyles.designationBadge}`}>
                                        {formatDesignation(user.designation)}
                                    </span>
                                </td>
                                <td>
                                    <span className={`${tableStyles.tableBadge} ${tableStyles[user.is_active ? 'statusActive' : 'statusInactive']}`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <div className={tableStyles.tableActions}>
                                        <button
                                            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon} ${styles.tooltipBtn}`}
                                            onClick={() => onEdit(user)}
                                            data-tooltip="Edit User"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        </button>
                                        <button
                                            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon} ${styles.tooltipBtn}`}
                                            onClick={() => onNavigatePerformance(user.id)}
                                            data-tooltip="View Performance"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 3v18h18"></path>
                                                <path d="M18 17V9"></path>
                                                <path d="M13 17V5"></path>
                                                <path d="M8 17v-3"></path>
                                            </svg>
                                        </button>
                                        {user.is_active ? (
                                            <button
                                                className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${buttonStyles.btnIcon} ${styles.tooltipBtn}`}
                                                onClick={() => onToggleStatus(user.id, true)}
                                                data-tooltip="Deactivate"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <line x1="15" y1="9" x2="9" y2="15"></line>
                                                    <line x1="9" y1="9" x2="15" y2="15"></line>
                                                </svg>
                                            </button>
                                        ) : (
                                            <button
                                                className={`${buttonStyles.btn} ${buttonStyles.btnSecondaryPrimary} ${buttonStyles.btnIcon} ${styles.tooltipBtn}`}
                                                onClick={() => onToggleStatus(user.id, false)}
                                                data-tooltip="Activate"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                                </svg>
                                            </button>
                                        )}
                                        <button
                                            className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${buttonStyles.btnIcon} ${styles.tooltipBtn}`}
                                            onClick={() => onDelete(user.id)}
                                            data-tooltip="Delete"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3,6 5,6 21,6"></polyline>
                                                <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* User Cards - Mobile/Tablet View */}
            <div className={styles.userCardsContainer}>
                {users.map((user) => (
                    <div key={user.id} className={styles.userCard} data-userid={user.id}>
                        {/* Card Header */}
                        <div className={styles.cardHeader}>
                            <div className={styles.cardCheckbox}>
                                <input
                                    type="checkbox"
                                    className={styles.rowCheckbox}
                                    checked={selectedUserIds.has(user.id)}
                                    onChange={(e) => onUserSelect(user.id, e.target.checked)}
                                />
                            </div>
                            <div className={styles.cardUserInfo}>
                                <div className={styles.userName}>{getUserName(user)}</div>
                                <div className={styles.userEmail}>{user.username || user.email}</div>
                                <div className={styles.userEmail}>{user.email}</div>
                            </div>
                            <div className={styles.cardActions}>
                                <button
                                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon} ${styles.tooltipBtn}`}
                                    onClick={() => onEdit(user)}
                                    data-tooltip="Edit User"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                </button>
                                <button
                                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon} ${styles.tooltipBtn}`}
                                    onClick={() => onNavigatePerformance(user.id)}
                                    data-tooltip="View Performance"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 3v18h18"></path>
                                        <path d="M18 17V9"></path>
                                        <path d="M13 17V5"></path>
                                        <path d="M8 17v-3"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Card Content */}
                        <div className={styles.cardContent}>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Designation:</span>
                                <span className={`${tableStyles.tableBadge} ${tableStyles.designationBadge}`}>
                                    {formatDesignation(user.designation)}
                                </span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Status:</span>
                                <span className={`${tableStyles.tableBadge} ${tableStyles[user.is_active ? 'statusActive' : 'statusInactive']}`}>
                                    {user.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>

                        {/* Card Footer Actions */}
                        <div className={styles.cardFooter}>
                            {user.is_active ? (
                                <button
                                    className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${styles.fullWidthBtn}`}
                                    onClick={() => onToggleStatus(user.id, true)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="15" y1="9" x2="9" y2="15"></line>
                                        <line x1="9" y1="9" x2="15" y2="15"></line>
                                    </svg>
                                    Deactivate
                                </button>
                            ) : (
                                <button
                                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondaryPrimary} ${styles.fullWidthBtn}`}
                                    onClick={() => onToggleStatus(user.id, false)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                    Activate
                                </button>
                            )}
                            <button
                                className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${styles.fullWidthBtn}`}
                                onClick={() => onDelete(user.id)}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3,6 5,6 21,6"></polyline>
                                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default UserTable;
